/**
 * paopao-stock.js — site-wide "already in your bag" stock guard.
 *
 * Tracks how many of each variant the customer already has in their cart and, when
 * that reaches the variant's real available stock, disables the relevant add control:
 *   • Quick-add (product cards): desktop button → "All available in bag" + disabled;
 *     mobile plus-icon button → hidden.
 *   • PDP add-to-cart CTA: handled by product.liquid, which listens for the
 *     `paopao:cart:updated` event this script dispatches.
 *
 * Demand counts BOTH plain line items AND charms/closures locked inside a PAOPAO
 * custom piece (the merged bundle line carries `_stock` = "handle:variantId:qty;…"),
 * so a charm that's fully committed to a custom build also blocks its standalone
 * quick-add — matching the configurator's own pre-checkout check.
 *
 * The cart is re-read after any /cart mutation (add/change/update/clear) via a thin
 * fetch wrapper, so every add control on the page stays in sync without each section
 * having to wire anything up.
 */
(function () {
  if (window.PaoPaoStock) return;

  var demand = {};        // numeric variant id (string) → quantity already committed
  var ready = false;

  /* ---------------- Demand map ---------------- */
  function buildDemand(cart) {
    var d = {};
    (cart && cart.items ? cart.items : []).forEach(function (it) {
      var stock = it.properties && it.properties._stock;
      if (stock) {
        // Merged custom piece: trust the per-component breakdown, skip the parent line.
        String(stock).split(';').forEach(function (entry) {
          var parts = entry.split(':');
          var vid = parts[1];
          var qty = parseInt(parts[2], 10) || 0;
          if (vid) d[vid] = (d[vid] || 0) + qty;
        });
      } else if (it.variant_id != null) {
        var vid2 = String(it.variant_id);
        d[vid2] = (d[vid2] || 0) + (it.quantity || 0);
      }
    });
    return d;
  }

  function inCart(variantId) {
    if (variantId == null) return 0;
    return demand[String(variantId)] || 0;
  }

  /* ---------------- Quick-add controls ---------------- */
  // limit = real stock cap (9999 = untracked/oversell → never blocks).
  // When every available unit is already in the bag we simply HIDE the card's quick-add
  // (desktop button + mobile plus alike) — no dead "Add to bag" button, no persistent
  // label. The card still links to the PDP, which shows the "All available in bag" CTA.
  function applyQuickAdd(el) {
    var limit = parseInt(el.getAttribute('data-stock-limit'), 10);
    if (!isFinite(limit)) limit = 9999;
    var soldInBag = limit < 9999 && (limit - inCart(el.getAttribute('data-variant-id'))) <= 0;
    el.classList.toggle('hidden', soldInBag);
  }

  function applyAll(scope) {
    (scope || document).querySelectorAll('[data-quick-add][data-stock-limit]').forEach(applyQuickAdd);
  }

  /* ---------------- Refresh on any cart change ---------------- */
  var refreshT;
  function refresh() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        demand = buildDemand(cart);
        ready = true;
        applyAll();
        document.dispatchEvent(new CustomEvent('paopao:cart:updated', { detail: { demand: demand, cart: cart } }));
      })
      .catch(function () {});
  }
  function scheduleRefresh() {
    clearTimeout(refreshT);
    refreshT = setTimeout(refresh, 120); // debounce bursts (add → drawer re-render)
  }

  // Re-read the cart after any mutation, wherever it originates.
  var origFetch = window.fetch.bind(window);
  window.fetch = function (input, opts) {
    var p = origFetch(input, opts);
    var url = typeof input === 'string' ? input : (input && input.url ? input.url : String(input));
    if (/\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
      p.then(function () { scheduleRefresh(); }).catch(function () {});
    }
    return p;
  };

  /* ---------------- New cards (search, recently viewed, etc.) ---------------- */
  var domT;
  var observer = new MutationObserver(function () {
    clearTimeout(domT);
    domT = setTimeout(function () { if (ready) applyAll(); }, 80);
  });

  window.PaoPaoStock = {
    demand: inCart,
    isReady: function () { return ready; },
    refresh: refresh,
    apply: applyAll,
  };

  function init() {
    observer.observe(document.body, { childList: true, subtree: true });
    refresh();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
